import { CheckIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import type { DiagnosedIcd } from 'types/result';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import {
  CATEGORY_DEFAULT_LABELS,
  resolveCategoryDisplay,
  resolveSicherheitDisplay,
  SICHERHEIT_DEFAULT_LABELS,
} from '../diagnosis.utils';
import { DiagnosisEntryRow } from './DiagnosisSection.styles';
import { DiagnosisClassificationSelects, type SeiteOption } from './DiagnosisClassificationSelects';
import { DiagnosisLoadingIndicator } from './DiagnosisLoadingIndicator';
import { SectionTitle } from 'widgets/results/ResultSection.styles';
import { SelfAnamnesisChip } from 'screens/result/result.styles';
import { MASKED_TEXT_CLASS, useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import type { AutoSaveStatus } from 'hooks/useDebouncedFieldAutosave';
import { useDiagnosedIcdsEditorContext } from 'providers/DiagnosedIcdsEditorProvider';
import { DiagnosisRelationshipBadge } from './DiagnosisRelationshipBadge';

/** The summary stores the side as a word; the letter is only what the doctor sees. */
const DIAGNOSED_ICD_SEITE_OPTIONS: SeiteOption[] = [
  { value: 'links', code: 'L', translationKey: 'links' },
  { value: 'rechts', code: 'R', translationKey: 'rechts' },
  { value: 'beidseitig', code: 'B', translationKey: 'beidseitig' },
  { value: 'keine_angabe', code: '-', translationKey: 'keine_angabe' },
];

type DiagnosisSectionProps = {
  title: string;
  autoStatus?: AutoSaveStatus;
  selectionAutoStatus?: AutoSaveStatus;
  placeholderText: string;
  pendingMoreDiagnoses?: boolean;
};

function formatSicherheitDescription(
  value: DiagnosedIcd['sicherheit'],
  translate: ReturnType<typeof useTranslation>['t']
): string {
  if (!value) {
    return translate('result.diagnosisSection.notSpecified');
  }
  const display = resolveSicherheitDisplay(value);
  if (!display) {
    return translate('result.diagnosisSection.notSpecified');
  }
  const label = translate(`result.diagnosisSection.sicherheit.${display.translationKey}`, {
    defaultValue: SICHERHEIT_DEFAULT_LABELS[display.translationKey],
  });
  return `${display.code} – ${label}`;
}

function formatSeiteDescription(
  value: DiagnosedIcd['seite'],
  translate: ReturnType<typeof useTranslation>['t']
): string {
  if (!value) {
    return translate('result.diagnosisSection.notSpecified');
  }
  return translate(`result.diagnosisSection.seite.${value}`, {
    defaultValue:
      value === 'links'
        ? 'links'
        : value === 'rechts'
          ? 'rechts'
          : value === 'beidseitig'
            ? 'beidseitig'
            : 'keine Angabe',
  });
}

function formatCategoryDescription(
  value: DiagnosedIcd['category'],
  translate: ReturnType<typeof useTranslation>['t']
): string {
  if (!value) {
    return translate('result.diagnosisSection.notSpecified');
  }
  const display = resolveCategoryDisplay(value);
  if (!display) {
    return translate('result.diagnosisSection.notSpecified');
  }
  const label = translate(`result.diagnosisSection.categoryValue.${display.translationKey}`, {
    defaultValue: CATEGORY_DEFAULT_LABELS[display.translationKey],
  });
  return `${display.code} – ${label}`;
}

export function DiagnosisSection({
  title,
  autoStatus,
  selectionAutoStatus,
  placeholderText,
  pendingMoreDiagnoses = false,
}: DiagnosisSectionProps) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const {
    editableDiagnosedIcds: diagnosedIcds,
    handleDiagnosedIcdChange: onChange,
    selectionStates,
    onToggleSelection,
  } = useDiagnosedIcdsEditorContext();
  const listToRender = diagnosedIcds;
  const hasStructuredIcds = listToRender.some((entry) => Boolean(entry.icdCode));

  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SectionTitle>{title}</SectionTitle>
        </div>
      </div>

      {listToRender.length > 0 ? (
        <div className="space-y-2">
          {hasStructuredIcds ? (
            <div className="overflow-hidden rounded-md border border-border bg-card">
              <div className="divide-y divide-border">
                {listToRender.map((entry, index) => {
                  const categoryDescription = formatCategoryDescription(entry.category, t);
                  const sicherheitDescription = formatSicherheitDescription(entry.sicherheit, t);
                  const seiteDescription = formatSeiteDescription(entry.seite, t);
                  const diagnosisLabel =
                    entry.icdCode?.title || t('result.diagnosisSection.notSpecified');
                  const selectionLabel = entry.code
                    ? t('result.diagnosisSection.selectDiagnosisWithCode', {
                        code: entry.code,
                      })
                    : t('result.diagnosisSection.selectDiagnosis');
                  const isUnselectable = !(entry.selectable ?? true);

                  return (
                    <DiagnosisEntryRow key={entry.id ?? index} $disabled={isUnselectable}>
                      <Checkbox
                        checked={selectionStates[index] ?? true}
                        onCheckedChange={(value) => {
                          const isCheckedNext = value === true;
                          trackEvent(ANALYTICS_EVENTS.RESULT_DIAGNOSIS_ENTRY_TOGGLED, {
                            checked: isCheckedNext,
                          });
                          onToggleSelection(index, isCheckedNext);
                        }}
                        disabled={!(entry.selectable ?? true)}
                        className="border border-muted-foreground shrink-0 mt-1 mr-2"
                        aria-label={selectionLabel}
                        data-ph-no-autocapture
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-16 shrink-0">
                            <span className="sr-only">
                              {t('result.diagnosisSection.codeLabel')}
                            </span>
                            <span
                              className={`flex w-full h-7 items-center justify-center rounded-md border border-input bg-white px-2 text-xs text-card-foreground ${MASKED_TEXT_CLASS}`}
                              aria-readonly="true"
                            >
                              {entry.code ?? ''}
                            </span>
                          </div>

                          <DiagnosisClassificationSelects
                            layout="compact"
                            withPlaceholder
                            category={resolveCategoryDisplay(entry.category)?.code ?? ''}
                            sicherheit={resolveSicherheitDisplay(entry.sicherheit)?.code ?? ''}
                            seite={entry.seite ?? ''}
                            seiteOptions={DIAGNOSED_ICD_SEITE_OPTIONS}
                            disabled={isUnselectable}
                            titles={{
                              category: categoryDescription,
                              sicherheit: sicherheitDescription,
                              seite: seiteDescription,
                            }}
                            onChange={(field, value) => onChange(index, field, value)}
                          />

                          {entry.isSelfAnamnesis ? (
                            <SelfAnamnesisChip>
                              {t('result.diagnosisSection.selfAnamnesis')}
                            </SelfAnamnesisChip>
                          ) : null}
                          <div className="ml-auto">
                            <DiagnosisRelationshipBadge diagnosedIcd={entry} />
                          </div>
                        </div>

                        <p
                          className={`mt-1 mx-1 text-sm break-words ${MASKED_TEXT_CLASS}`}
                          title={diagnosisLabel}
                        >
                          {diagnosisLabel}
                        </p>
                      </div>
                    </DiagnosisEntryRow>
                  );
                })}
              </div>
            </div>
          ) : (
            listToRender.map((entry, index) => (
              <div
                key={entry.id ?? index}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border border-border bg-muted px-3 py-2 text-xs"
              >
                <div className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                  <span>{t('result.diagnosisSection.codeLabel')}</span>
                  <span
                    className={`flex w-24 items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-card-foreground ${MASKED_TEXT_CLASS}`}
                    aria-readonly="true"
                  >
                    {entry.code ?? ''}
                  </span>
                </div>
                <DiagnosisClassificationSelects
                  layout="labeled"
                  withPlaceholder
                  category={resolveCategoryDisplay(entry.category)?.code ?? ''}
                  sicherheit={resolveSicherheitDisplay(entry.sicherheit)?.code ?? ''}
                  seite={entry.seite ?? ''}
                  seiteOptions={DIAGNOSED_ICD_SEITE_OPTIONS}
                  onChange={(field, value) => onChange(index, field, value)}
                />
                {entry.icdCode?.title ? (
                  <p className="basis-full text-xs text-gray-500">{entry.icdCode.title}</p>
                ) : null}
              </div>
            ))
          )}
          {pendingMoreDiagnoses ? <DiagnosisLoadingIndicator /> : null}
          {autoStatus === 'saved' || selectionAutoStatus === 'saved' ? (
            <div className="flex justify-end pt-1">
              <span
                className="flex items-center gap-1 rounded bg-background/80 px-1 py-0.5 text-xs text-success"
                title={t('result.autoSaveSuccessful')}
              >
                <CheckIcon className="h-4 w-4" />
                <span>{t('result.autoSaveSuccessful')}</span>
              </span>
            </div>
          ) : null}
          {autoStatus === 'error' || selectionAutoStatus === 'error' ? (
            <p className="text-xs text-red-600">{t('result.savedFailed')}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{placeholderText}</p>
      )}
    </div>
  );
}
